import { savePalette } from '@/api/color-library';
import { Dropdown, Link, MetadataContext, Modal, useNotification } from '@/components';
import { Color, Palette } from '@/types/ColorLibraryModel';
import { fixColor, hexToRgb, isColorDark } from '@/utils';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useContext, useEffect, useMemo, useState } from 'react';
import XLight from '@/assets/xLight.svg';
import { SaveToScrapbookButton } from '@/components/scrapbook';
import { ScrapbookContentType } from '@/types';
import tailwindConfig from '@/tailwind.screens';

type ColourStripDetailsModel = {
  color: Color;
  palettes: Palette[];
  onClose: () => void;
};

const ColourStripDetails = (data: ColourStripDetailsModel) => {
  const { color, palettes, onClose } = data;
  const [chosenPaletteId, setChosenPaletteId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [modalOpen, setModalOpen] = useState(true);
  const { showNotification } = useNotification();
  const t = useTranslations();
  const metadataContext = useContext(MetadataContext);

  const isDark = useMemo(() => isColorDark(fixColor(color.hexCode)), [color.hexCode]);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < parseInt(tailwindConfig.screens.sm, 10));
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onAddToPaletteClick = async () => {
    if (!chosenPaletteId) return;

    const palette = palettes.find((p) => String(p.id) === chosenPaletteId);
    if (!palette) return;

    if (palette.colours.find((c) => c.id === color.id)) return;
    palette.colours.push(color);

    savePalette(palette).then(({ success }) => {
      if (success) {
        showNotification('Color was added to palette successfully', 'success');
      }
    });
  };

  const content = (
    <div
      className={classNames(
        'mg:gap-0 relative flex w-full flex-col-reverse gap-6 bg-cover bg-center bg-no-repeat px-7 pt-10 pb-3 transition-all md:flex-row md:px-10 md:py-10 lg:px-24 lg:pt-18 lg:pb-13',
        'rounded-lg md:rounded-none',
        isDark ? 'module-base-light' : 'module-base-dark',
        color.effect === 'Metallic' && 'bg-metallic',
        color.effect === 'Lustre' && 'bg-lustre',
        color.effect === 'Chrome' && 'bg-chrome',
      )}
      style={{
        backgroundColor: fixColor(color.hexCode),
      }}
    >
      <button
        className="btn icon dark absolute -top-4 -right-4 !hidden cursor-pointer !p-1 md:!inline-flex"
        onClick={onClose}
      >
        <XLight className="h-7 w-7" />
      </button>

      <div className="flex w-full flex-col gap-8 pt-3 md:w-5/12 xl:w-4/12">
        <h3 className="color-from-bg/70 hidden text-lg leading-6 font-light md:block lg:text-[26px] lg:leading-8">
          {color.displayName}
        </h3>
        <div className="border-from-bg/10 flex min-h-[34px] items-center rounded-full border">
          <Dropdown
            placeholder={t('colourLibrary.$palette')}
            name="palette"
            options={[...palettes.map((p) => ({ label: p.name, value: String(p.id) }))]}
            onChange={(e) => setChosenPaletteId(e.target.value || '')}
            className={classNames(
              'w-1/2 px-6 py-2',
              '[&_.main-button]:h-auto [&_.main-button]:w-full [&_.main-button]:cursor-pointer [&_.main-button]:border-none [&_.main-button]:bg-transparent [&_.main-button]:p-0 [&_.main-button]:text-center [&_.main-button]:outline-none',
              '[&_.arrow_svg]:duration-0',
              isDark
                ? '[&_.arrow_svg]:fill-white [&_.popover-title]:text-white [&_.popover-title.placeholder]:text-white/70'
                : '[&_.popover-title]:text-black [&_.popover-title.placeholder]:text-black/60',
            )}
            contentWrapperClassName="[&_.content]:bg-white [&_.content]:text-black w-full [&_.content]:w-full [&_.option]:text-lg"
            offset={8}
          />
          <div className="border-from-bg/10 h-[18px] border-r"></div>
          <button
            onClick={onAddToPaletteClick}
            disabled={!chosenPaletteId}
            className={classNames(
              chosenPaletteId && 'cursor-pointer',
              'color-from-bg w-1/2 px-5 font-semibold whitespace-nowrap transition-all duration-0 hover:opacity-80 disabled:opacity-50',
            )}
          >
            {t('colourLibrary.$addToPalette')}
          </button>
        </div>
        <Link
          className="btn border-from-bg/10 color-from-bg w-full border !text-sm !font-semibold !tracking-normal !duration-0 hover:bg-white/10 md:!text-base"
          link={{
            href: '/tools/colour-library',
            target: '_blank',
            text: t('colourLibrary.$createapaletteinColourLibrary'),
          }}
        />
        <div className="flex justify-between">
          <div>
            <SaveToScrapbookButton
              scrapbookContent={{
                id: color.id,
                displayName: color.displayName,
                group: color.group,
                tpxCode: color.tpxCode,
                coatedCode: color.coatedCode,
                ralCode: color.ralCode,
                ncsCode: color.ncsCode,
                hexCode: color.hexCode,
                effect: color.effect,
                seasons: color.seasons,
                pageId: metadataContext.pageId,
                pageUrl: metadataContext.pageUrl,
                pageTitle: metadataContext.pageTitle,
              }}
              scrapbookContentType={ScrapbookContentType.Colour}
              scrapbookPath={`colour.${color.id}.json`}
              successMessage="Color was added to scrapbook successfully"
              tooltipProps={{ content: 'Save to Scrapbook', placement: 'right' }}
              className="btn icon btn-from-bg transparent !p-1.5 [&_svg]:h-7 [&_svg]:w-7"
            />
          </div>
          <button onClick={() => setModalOpen(false)} className="color-from-bg text-sm md:hidden">
            {t('colourLibrary.$close')}
          </button>
        </div>
      </div>

      <div className="flex w-full flex-row gap-6 md:w-7/12 md:flex-col md:p-3 xl:w-8/12">
        <div className="color-from-bg/70 flex w-full flex-col justify-end gap-4 md:flex-row md:gap-0">
          <div className="w-full md:w-3/12">
            <span className="text-xs leading-[18px] font-semibold">{t('colourLibrary.$pantone')}</span>
            <div className="text-base leading-[22px]">{color.tpxCode}</div>
          </div>
          <div className="w-full md:w-3/12">
            <span className="text-xs leading-[18px] font-semibold">{t('colourLibrary.$hex')}</span>
            <div className="text-base leading-[22px]">{fixColor(color.hexCode)}</div>
          </div>
          <div className="w-full md:w-3/12">
            <span className="text-xs leading-[18px] font-semibold">{t('colourLibrary.$ral')}</span>
            <div className="text-base leading-[22px]">{color.ralCode}</div>
          </div>
        </div>
        <div className="color-from-bg/70 flex w-full flex-col justify-end gap-4 md:flex-row md:gap-0">
          <div className="w-full md:w-3/12">
            <span className="text-xs leading-[18px] font-semibold">{t('colourLibrary.$coated')}</span>
            <div className="text-base leading-[22px]">{color.coatedCode}</div>
          </div>
          <div className="w-full md:w-3/12">
            <span className="text-xs leading-[18px] font-semibold">{t('colourLibrary.$rgb')}</span>
            <div className="text-base leading-[22px]">{hexToRgb(color.hexCode)?.join('/')}</div>
          </div>
          <div className="w-full md:w-3/12">
            <span className="text-xs leading-[18px] font-semibold">{t('colourLibrary.$ncs')}</span>
            <div className="text-base leading-[22px]">{color.ncsCode}</div>
          </div>
        </div>
      </div>

      <h3 className="color-from-bg text-lg leading-6 font-semibold md:hidden lg:text-[26px] lg:leading-8">
        {color.displayName}
      </h3>
    </div>
  );

  return isMobile ? (
    <Modal open={modalOpen} onClose={onClose} contanerClassName="w-full max-w-[80vw] shadow-xl">
      {content}
    </Modal>
  ) : (
    content
  );
};

export default ColourStripDetails;
